import React, { useEffect, useRef, useState } from 'react';
import { EVERYONE, TEAM } from '../../constants/team';
import { formatDay, STATUSES } from './board';

export const errorText = (err) => {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return 'Keine Berechtigung – bitte neu anmelden.';
  return err?.response?.data?.message || 'Speichern fehlgeschlagen – bitte noch einmal versuchen.';
};

/** The frame both editors share: a card over the page that Escape or the scrim closes. */
function Dialog({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="tp-scrim" onClick={onClose} aria-hidden="true" />
      <div className="tp-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="tp-dialog__head">
          <h2 className="tp-dialog__title">{title}</h2>
          <button type="button" className="tp-dialog__close" onClick={onClose} aria-label="Schließen">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

/** Delete in two steps, so a stray click does not lose anything. */
function DeleteButton({ label, question, onDelete, disabled }) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button type="button" className="tp-btn tp-btn--danger-ghost" onClick={() => setAsking(true)} disabled={disabled}>
        {label}
      </button>
    );
  }
  return (
    <span className="tp-confirm">
      <span className="tp-confirm__text">{question}</span>
      <button type="button" className="tp-btn tp-btn--danger" onClick={onDelete} disabled={disabled}>Ja, löschen</button>
      <button type="button" className="tp-btn" onClick={() => setAsking(false)} disabled={disabled}>Nein</button>
    </span>
  );
}

/** Create or edit a task: what, who, by when, for which milestone, and in which column. */
export function TaskDialog({ task, milestones, defaults = {}, onSave, onDelete, onClose }) {
  const isNew = !task?.id;
  const [form, setForm] = useState(() => ({
    title: task?.title || '',
    description: task?.description || '',
    assignee: task?.assignee || defaults.assignee || '',
    dueDate: task?.dueDate || '',
    milestoneId: task?.milestoneId ?? defaults.milestoneId ?? '',
    status: task?.status || defaults.status || 'OPEN',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef(null);

  useEffect(() => { titleRef.current?.focus(); }, []);

  const set = (field) => (event) => { setForm((f) => ({ ...f, [field]: event.target.value })); setError(''); };
  const milestone = milestones.find((m) => String(m.id) === String(form.milestoneId));

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) { setError('Bitte einen Titel eingeben.'); return; }
    setBusy(true);
    try {
      await onSave({
        title: form.title.trim(),
        description: form.description.trim() || null,
        assignee: form.assignee || null,
        dueDate: form.dueDate || null,
        milestoneId: form.milestoneId === '' ? null : Number(form.milestoneId),
        status: form.status,
      });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onDelete();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title={isNew ? 'Neue Aufgabe' : 'Aufgabe bearbeiten'} onClose={onClose}>
      <form className="tp-form" onSubmit={save}>
        <label className="tp-field">
          <span className="tp-field__label">Was ist zu tun?</span>
          <input ref={titleRef} className="tp-input" value={form.title} onChange={set('title')} maxLength={200}
            placeholder="z. B. Screenshots für den App Store" />
        </label>

        <div className="tp-form__row">
          <label className="tp-field">
            <span className="tp-field__label">Wer übernimmt?</span>
            <select className="tp-input" value={form.assignee} onChange={set('assignee')}>
              <option value="">Noch niemand</option>
              {TEAM.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
              <option value={EVERYONE}>Alle gemeinsam</option>
            </select>
          </label>
          <label className="tp-field">
            <span className="tp-field__label">Fertig bis</span>
            <input type="date" className="tp-input" value={form.dueDate} onChange={set('dueDate')} />
          </label>
        </div>

        <div className="tp-form__row">
          <label className="tp-field">
            <span className="tp-field__label">Meilenstein</span>
            <select className="tp-input" value={form.milestoneId} onChange={set('milestoneId')}>
              <option value="">Keiner</option>
              {milestones.map((m) => <option key={m.id} value={m.id}>{m.title} (bis {formatDay(m.dueDate)})</option>)}
            </select>
          </label>
          <label className="tp-field">
            <span className="tp-field__label">Stand</span>
            <select className="tp-input" value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        </div>
        {milestone && form.dueDate && form.dueDate > milestone.dueDate && (
          <p className="tp-hint tp-hint--warn">Fällig erst nach der Deadline des Meilensteins ({formatDay(milestone.dueDate)}).</p>
        )}

        <label className="tp-field">
          <span className="tp-field__label">Details <span className="tp-field__optional">optional</span></span>
          <textarea className="tp-input tp-input--area" value={form.description} onChange={set('description')}
            maxLength={4000} rows={4} placeholder="Links, Absprachen, was „fertig“ heißt …" />
        </label>

        {error && <p className="tp-error" role="alert">{error}</p>}

        <div className="tp-form__actions">
          {!isNew && <DeleteButton label="Löschen" question="Aufgabe löschen?" onDelete={remove} disabled={busy} />}
          <span className="tp-form__spacer" />
          <button type="button" className="tp-btn" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button type="submit" className="tp-btn tp-btn--primary" disabled={busy}>
            {isNew ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Create or edit a milestone: a goal with a deadline. */
export function MilestoneDialog({ milestone, onSave, onDelete, onClose }) {
  const isNew = !milestone?.id;
  const [form, setForm] = useState(() => ({
    title: milestone?.title || '',
    dueDate: milestone?.dueDate || '',
    description: milestone?.description || '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef(null);

  useEffect(() => { titleRef.current?.focus(); }, []);

  const set = (field) => (event) => { setForm((f) => ({ ...f, [field]: event.target.value })); setError(''); };

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) { setError('Bitte einen Namen eingeben.'); return; }
    if (!form.dueDate) { setError('Bitte eine Deadline wählen.'); return; }
    setBusy(true);
    try {
      await onSave({ title: form.title.trim(), dueDate: form.dueDate, description: form.description.trim() || null });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onDelete();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title={isNew ? 'Neuer Meilenstein' : 'Meilenstein bearbeiten'} onClose={onClose}>
      <form className="tp-form" onSubmit={save}>
        <div className="tp-form__row tp-form__row--wide">
          <label className="tp-field">
            <span className="tp-field__label">Ziel</span>
            <input ref={titleRef} className="tp-input" value={form.title} onChange={set('title')} maxLength={200}
              placeholder="z. B. App-Store-Launch" />
          </label>
          <label className="tp-field">
            <span className="tp-field__label">Deadline</span>
            <input type="date" className="tp-input" value={form.dueDate} onChange={set('dueDate')} />
          </label>
        </div>
        <label className="tp-field">
          <span className="tp-field__label">Worum geht’s? <span className="tp-field__optional">optional</span></span>
          <textarea className="tp-input tp-input--area" value={form.description} onChange={set('description')}
            maxLength={2000} rows={3} />
        </label>

        {error && <p className="tp-error" role="alert">{error}</p>}

        <div className="tp-form__actions">
          {!isNew && (
            <DeleteButton label="Löschen" question="Löschen? Die Aufgaben bleiben." onDelete={remove} disabled={busy} />
          )}
          <span className="tp-form__spacer" />
          <button type="button" className="tp-btn" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button type="submit" className="tp-btn tp-btn--primary" disabled={busy}>
            {isNew ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
